import * as R from 'remeda';
import { z } from 'zod';
import { SaveAutomationTargetPayload } from '@rossum/api-client/queues';
import { Theme } from '@rossum/ui/material';
import { OptimizationData } from '../core/types';
import {
  convertErrorRateToPrecisionTarget,
  createOptimizationSettings,
  getLegacyThresholdsToSave,
  unifyDecimalSeparator,
} from '../core/utils';
import { CustomPrecision } from './types';

export const CHART_HEIGHT = 160;

export const getLineSelectTransition = (theme: Theme, element: string[]) =>
  theme.transitions.create(element, {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  });

// coerce.number converts "" into 0 which causes problems
export const precisionSchema = z.preprocess(val => {
  if (val === '') return undefined;

  return typeof val === 'string' ? unifyDecimalSeparator(val) : val;
}, z.coerce.number().min(0).max(100).optional());

/**
 * A function that computes the automation targets to be saved. The function computes the data based on
 * - wether the user has anything saved before
 * - user has entered custom values for precision targets of schema fields (typically in some form)
 * - the optimization target selected by the user
 *
 * `legacy_thresholds`: These are the existing confidence thresholds in the queue schema at the time of saving the first automation target.
 *
 * `automation_assistant_v1`: These are the new confidence thresholds generated by the automation assistant.
 * @param targetState - an automation optimization target computed by the automation assistant
 * @param currentState - the current automation state based on queue history, without assistant optimization
 * @param defaultScoreThreshold - the fallback threshold value to be used in case the score thresold value in queue schema is nullish. Used to compute the legacy_thresholds
 * @param isSavingForTheFirstTime - a boolean that determines whether to return `legacy_threshold` or not.
 * @param thresholdMapFromSchema - a map of confidence threshold values for the schema fields in queue schema. Used to compute the legacy_thresholds
 * @returns automation targets to be saved by the user. Depending on whether its the first time the user is saving or not, the function either returns legacy_thresholds or not
 *
 */
export const getAutomationTargetsToSave = ({
  targetState,
  currentState,
  thresholdMapFromSchema,
  isSavingForTheFirstTime,
  userDefinedPrecisionTargets,
  defaultScoreThreshold,
}: {
  targetState: OptimizationData;
  currentState: OptimizationData;
  thresholdMapFromSchema: Record<string, number | null>;
  isSavingForTheFirstTime: boolean;
  userDefinedPrecisionTargets: CustomPrecision;
  defaultScoreThreshold: number;
}): SaveAutomationTargetPayload[] => {
  const customPrecision = R.pipe(
    userDefinedPrecisionTargets,
    R.entries(),
    R.flatMap(([schemaId, precision]) => {
      const parsed = precisionSchema.safeParse(precision);
      return parsed.success && parsed.data !== undefined
        ? [[schemaId, parsed.data / 100] as const]
        : [];
    }),
    R.fromEntries()
  );

  const automationTargetToSave = createOptimizationSettings({
    data: targetState,
    customPrecision,
    type: 'automation_assistant_v1',
  });

  // if nothing was saved before we save legacy_thresholds along with automation target
  if (isSavingForTheFirstTime) {
    const legacyThresholds = getLegacyThresholdsToSave({
      currentState,
      defaultScoreThreshold,
      thresholdMapFromSchema,
    });
    return [legacyThresholds, automationTargetToSave];
  }

  return [automationTargetToSave];
};

export const convertErrorRatesIntoPrecisionTargets = (errorRates: number[]) =>
  errorRates.map(convertErrorRateToPrecisionTarget);
